import { useRef } from "react";
import { AgTechButton, useClickable } from "@agtech/web/components/Buttons/AgTechButtons";
import AgTechCard from "@agtech/web/components/Cards/AgTechCard";
import AgTechHeaderDetail from "@agtech/web/components/Headers/AgTechHeaderDetail";
import AgTechIconBadge from "@agtech/web/components/Icons/AgTechIconBadge";
import { AgTechVerticalPopUpMenu, AgTechVerticalPopUpMenuSection } from "@agtech/web/components/Menus/AgTechVerticalPopUpMenu";
import { ReportDownloadFormat } from "@sales/data/reports/ReportData";

export type ReportCardProps = {
    reportName: string,
    reportDescription: string,
    reportIcon: string,
    reportDownloadAction: () => Promise<any>,
    reportDownloadFormats?: ReportDownloadFormat[]
}

type SplitDownloadButtonProps = {
    reportName: string,
    downloadAction: () => Promise<any>,
    formats: ReportDownloadFormat[]
}

const ReportDownloadSplitButton = (props: SplitDownloadButtonProps) => {
    const mainRef = useRef(null);
    const chevronRef = useRef(null);
    const mainClickable = useClickable(mainRef);
    const chevronClickable = useClickable(chevronRef);
    const downloadLabel = 'Downloading report: ' + props.reportName;

    return (
        <div className='ml-auto flex items-stretch'>
            <button
                ref={mainRef}
                className='button button-highlight !rounded-r-none'
                onClick={() => mainClickable.actions.executeWithVisual(
                    props.downloadAction,
                    { message: downloadLabel, delay: 1000 }
                )}
            >
                <i className='fas fa-download mr-2 w-[20px]' style={{ pointerEvents: 'none' }} />
                Download
            </button>
            <button
                ref={chevronRef}
                className='button button-highlight !rounded-l-none border-l border-white/25 px-3'
                onClick={e => {
                    e.stopPropagation();
                    chevronClickable.renderPopup(() => (
                        <AgTechVerticalPopUpMenu>
                            <AgTechVerticalPopUpMenuSection header='Download As'>
                                {props.formats.map(format => (
                                    <AgTechButton
                                        key={format.label}
                                        text={format.label}
                                        icon={format.icon}
                                        action={async e => await e.actions.executeWithVisual(
                                            format.action,
                                            { message: downloadLabel, delay: 1000 }
                                        )}
                                    />
                                ))}
                            </AgTechVerticalPopUpMenuSection>
                        </AgTechVerticalPopUpMenu>
                    ));
                }}
            >
                <i className='fas fa-chevron-down text-xs' style={{ pointerEvents: 'none' }} />
            </button>
        </div>
    );
}

const ReportCard = (props: ReportCardProps) => {
    const downloadLabel = 'Downloading report: ' + props.reportName;

    return (
        <AgTechCard
            content={{
                header: () => (
                    <AgTechHeaderDetail
                        header={props.reportName}
                        settings={{
                            classes: 'flex items-center',
                            header: {
                                classes: '!text-more'
                            }
                        }}
                    >
                        <AgTechIconBadge
                            icon={props.reportIcon}
                            size={30}
                            iconClasses="text-sm"
                        />
                    </AgTechHeaderDetail>
                ),
                footer: () => props.reportDownloadFormats ? (
                    <ReportDownloadSplitButton
                        reportName={props.reportName}
                        downloadAction={props.reportDownloadAction}
                        formats={props.reportDownloadFormats}
                    />
                ) : (
                    <AgTechButton
                        text='Download'
                        icon='fas fa-download'
                        action={async e => await e.actions.executeWithVisual(
                            props.reportDownloadAction,
                            { message: downloadLabel, delay: 1000 },
                        )}
                        classes='ml-auto button-highlight'
                    />
                )
            }}
            structure={{
                height: 180
            }}
        >
            <h1 className='font-size-standard font-color-secondary'>
                {props.reportDescription}
            </h1>
        </AgTechCard>
    )
}

export default ReportCard;